// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canApplyPromotionsToCart,
    getCartState,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import removePromotions from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotions";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const promotions = promotionsDataView.value;
    const {
        isApplyingPromotion,
        promotionErrorMessage,
        promotionSuccessMessage,
        isCheckingOutWithPayPay,
        isPromotionAppliedSuccess,
        isPromotionRemovedSuccess,
        isPromotionsRemovedSuccess,
    } = state.pages.checkoutReviewAndSubmit;
    return {
        promotions,
        isApplyButtonDisabled: cartState.isLoading || isApplyingPromotion || isCartEmpty(cart),
        canApplyPromosToCart: canApplyPromotionsToCart(cart) && !isCheckingOutWithPayPay,
        promotionErrorMessage,
        promotionSuccessMessage,
        isPromotionAppliedSuccess,
        isPromotionRemovedSuccess,
        isPromotionsRemovedSuccess,
    };
};

const mapDispatchToProps = {
    applyPromotion,
    removePromotion,
    removePromotions,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitPromotionCodeStyles {
    form?: InjectableCss;
    promotionCodeTextField?: TextFieldPresentationProps;
    applyButton?: ButtonPresentationProps;
    applySuccessText?: TypographyPresentationProps;
    promotionRemovedText?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    promotionCodeGridItem?: GridItemProps;
    applyPromotionGridItem?: GridItemProps;
    promotionAppliedGridItem?: GridItemProps;
    promotionRemovedGridItem?: GridItemProps;
    removeCodesGridItem?: GridItemProps;
    removeCodeGridItem?: GridItemProps;
    removeCodesLink?: LinkPresentationProps;
    removeCodeTag?: TagPresentationProps;
}

export const promotionCodeFormStyles: CheckoutReviewAndSubmitPromotionCodeStyles = {
    applyButton: {
        variant: "secondary",
        css: css`
            position: absolute;
            top: 28px;
            right: 5px;
            min-height: 32px;
            height: 32px;
            padding: 0 15px;
        `,
    },
    gridContainer: { gap: 10 },
    promotionCodeGridItem: {
        width: [12, 12, 12, 8, 8],
    },
    promotionCodeTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 100%;
                input {
                    padding-right: 85px;
                }
            `,
        },
    },
    form: {
        css: css`
            width: 100%;
            max-width: 100%;
        `,
    },
    promotionAppliedGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    promotionRemovedGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    removeCodesGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    removeCodeGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-wrap: wrap;
        `,
    },
    removeCodesLink: {
        color: "primary",
    },
    removeCodeTag: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
    },
};

const styles = promotionCodeFormStyles;
const StyledForm = getStyledWrapper("form");

const CheckoutReviewAndSubmitPromotionCode = ({
    promotions,
    isApplyButtonDisabled,
    promotionSuccessMessage,
    promotionErrorMessage,
    isPromotionAppliedSuccess,
    isPromotionRemovedSuccess,
    isPromotionsRemovedSuccess,
    canApplyPromosToCart,
    applyPromotion,
    removePromotion,
    removePromotions,
}: Props) => {
    const { promotionCode, setPromotionCode, promotionsWithCode, removingPromotionCode, setRemovingPromotionCode } =
        usePromotionCodeForm(promotions);

    const handlePromotionCodeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setPromotionCode(event.currentTarget.value);

    const applyPromotionHandler = () => {
        applyPromotion({ promotionCode: promotionCode.trim() });
        setPromotionCode("");
    };

    // Xng-Change on enter click call apply promotion
    // const handlePromotionCodeChange = () => {
    //     applyPromotion({ promotionCode });
    //     setPromotionCode("");
    // }

    const removePromotionCodesHandler = () => {
        removePromotions();
    };

    const removePromotionHandler = (promotion: PromotionModel) => {
        removePromotion({ promotionId: promotion.id });
        setRemovingPromotionCode(promotion.promotionCode);
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.preventDefault();
            if (promotionCode && promotionCode.length > 0) {
                applyPromotionHandler();
            }
        }
    };

    if (!canApplyPromosToCart) {
        return null;
    }

    return (
        <>
            <StyledWrapper
                css={css`
                    position: relative;
                    max-width: 100%;
                    display: flex;
                `}
            >
                <TextField
                    {...styles.promotionCodeTextField}
                    label={translate("Have a Promotion Code?")}
                    value={promotionCode}
                    onChange={handlePromotionCodeChange}
                    data-test-selector="promotionCodeTextField"
                    placeholder={translate("Promotion Code")}
                    onKeyPress={keyPressHandler}
                />
                <Button
                    aria-label={translate("Apply promotion code")}
                    {...styles.applyButton}
                    type="button"
                    disabled={isApplyButtonDisabled || promotionCode.length === 0}
                    data-test-selector="applyPromotionCodeButton"
                    onClick={e => {
                        e.preventDefault();
                        applyPromotionHandler();
                    }}
                >
                    {translate("Apply")}
                </Button>
            </StyledWrapper>
            {isPromotionAppliedSuccess && (
                <GridItem {...styles.promotionAppliedGridItem}>
                    <Typography
                        color="success"
                        size={fontConfig.fontSize.regular}
                        weight={fontConfig.weight.regular}
                        lineHeight={fontConfig.lineHeight.singleLinexSmall}
                        css={css`
                            margin-top: 8px;
                            max-width: 100%;
                        `}
                        as="div"
                        data-test-selector="promotionCode_addingPromotionCodeSuccessMessage"
                    >
                        {promotionSuccessMessage}
                    </Typography>
                </GridItem>
            )}
            {promotionsWithCode.length > 0 && (
                <GridItem {...styles.removeCodesGridItem}>
                    <Link
                        {...styles.removeCodesLink}
                        onClick={e => {
                            e.preventDefault();
                            removePromotionCodesHandler();
                        }}
                        data-test-selector="promotionCode_removePromotionCodesButton"
                    >
                        {translate("Remove Codes")}
                    </Link>
                </GridItem>
            )}
            {promotionsWithCode.length > 0 && (
                <GridItem {...styles.removeCodeGridItem}>
                    {promotionsWithCode.map(promotion => (
                        <Tag
                            {...styles.removeCodeTag}
                            key={promotion.id}
                            onDelete={e => {
                                e.preventDefault();
                                removePromotionHandler(promotion);
                            }}
                            data-test-selector={`promotionCode_promotionTag_${promotion.promotionCode}`}
                        >
                            {promotion.promotionCode}
                        </Tag>
                    ))}
                </GridItem>
            )}
            {isPromotionsRemovedSuccess && (
                <GridItem {...styles.promotionRemovedGridItem}>
                    <Typography
                        color="success"
                        size={fontConfig.fontSize.regular}
                        weight={fontConfig.weight.regular}
                        lineHeight={fontConfig.lineHeight.singleLinexSmall}
                        css={css`
                            margin-top: 8px;
                            max-width: 100%;
                        `}
                        as="div"
                        data-test-selector="promotionCode_allPromotionCodesRemovedMessage"
                    >
                        {siteMessage("Promotion_CodesRemoved")}
                    </Typography>
                </GridItem>
            )}
            {isPromotionRemovedSuccess && (
                <GridItem {...styles.promotionRemovedGridItem}>
                    <Typography
                        {...styles.promotionRemovedText}
                        color="success"
                        size={fontConfig.fontSize.regular}
                        weight={fontConfig.weight.regular}
                        lineHeight={fontConfig.lineHeight.singleLinexSmall}
                        css={css`
                            margin-top: 8px;
                            max-width: 100%;
                        `}
                        as="div"
                        data-test-selector="promotionCode_promotionCodeRemovedMessage"
                    >
                        {siteMessage("Promotion_CodeRemoved", removingPromotionCode)}
                    </Typography>
                </GridItem>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPromotionCode),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Promotion Code Form",
    },
};

export default widgetModule;
